import { type ComponentType } from 'react'

import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import {
  type FrameBuilderConfigurationWithLimits,
  type FrameBuilderState,
  type FrameBuilderStep,
  type FrameBuilderStepState,
} from './types'

import ArtworkImageDefinition from '@frame-builder/components/definitions/artwork-image-definition'
import ArtworkSizeDefinition from '@frame-builder/components/definitions/artwork-size-definition'
import FrameStyleDefinition from '@frame-builder/components/definitions/frame-style-definition'
import GlassDefinition from '@frame-builder/components/definitions/glass-definition'
import PassepartoutDefinition from '@frame-builder/components/definitions/passepartout-definition'
import SpacerDefinition from '@frame-builder/components/definitions/spacer-definition'
import ArtworkImageStepPreview from '@frame-builder/components/previews/artwork-image-step-preview'
import ArtworkSizeStepPreview from '@frame-builder/components/previews/artwork-size-step-preview'
import FrameStyleStepPreview from '@frame-builder/components/previews/frame-style-step-preview'
import GlassStepPreview from '@frame-builder/components/previews/glass-step-preview'
import PassepartoutStepPreview from '@frame-builder/components/previews/passepartout-step-preview'
import SpacerStepPreview from '@frame-builder/components/previews/spacer-step-preview'

export interface StepDefinitionProps {
  stepNumber: number
  onClose: () => void
  className?: string
}

export interface StepPreviewProps {
  stepNumber: number
  openStep: FrameBuilderStep | null
  onToggleStep: (step: FrameBuilderStep, isStepOpen: boolean) => void
}

export interface StepDefinition {
  stepNumber: number
  step: FrameBuilderStep
  previewComponent: ComponentType<StepPreviewProps>
  definitionComponent: ComponentType<StepDefinitionProps>
}

export const defaultStepStates: Record<
  FrameBuilderStep,
  FrameBuilderStepState
> = {
  artworkSize: 'closed',
  artworkImage: 'closed',
  frameStyle: 'closed',
  passepartout: 'closed',
  glass: 'closed',
  spacer: 'closed',
}

/**
 * Gets definitions of all enabled builder steps.
 */
export const getFilteredStepDefinitions = (
  stepDefinitions: StepDefinition[],
  filterCallback: (stepDefinition: StepDefinition) => boolean,
): StepDefinition[] => {
  const filteredStepDefinitions = stepDefinitions.filter(filterCallback)

  // Update step numbers
  return filteredStepDefinitions.map((stepDefinition, index) => {
    const newStepDefinition: StepDefinition = {
      ...stepDefinition,
      stepNumber: index + 1,
    }

    return newStepDefinition
  })
}

/**
 * Gets definitions of all enabled builder steps.
 */
export const getAllStepDefinitions = (): StepDefinition[] => {
  // Create definitions with placeholder step numbers
  const stepDefinitions: StepDefinition[] = [
    {
      stepNumber: 0,
      step: 'artworkSize',
      previewComponent: ArtworkSizeStepPreview,
      definitionComponent: ArtworkSizeDefinition,
    },
    {
      stepNumber: 0,
      step: 'artworkImage',
      previewComponent: ArtworkImageStepPreview,
      definitionComponent: ArtworkImageDefinition,
    },
    {
      stepNumber: 0,
      step: 'frameStyle',
      previewComponent: FrameStyleStepPreview,
      definitionComponent: FrameStyleDefinition,
    },
    {
      stepNumber: 0,
      step: 'passepartout',
      previewComponent: PassepartoutStepPreview,
      definitionComponent: PassepartoutDefinition,
    },
    {
      stepNumber: 0,
      step: 'glass',
      previewComponent: GlassStepPreview,
      definitionComponent: GlassDefinition,
    },
    {
      stepNumber: 0,
      step: 'spacer',
      previewComponent: SpacerStepPreview,
      definitionComponent: SpacerDefinition,
    },
  ]

  // Filter results to update step numbers
  return getFilteredStepDefinitions(stepDefinitions, () => true)
}

/**
 * Gets frame builder step states with only finished animations.
 */
export const getAnimationFinishedStepStates = (
  configurationStep: FrameBuilderStep | null,
) => {
  const stepStates: Record<FrameBuilderStep, FrameBuilderStepState> = {
    artworkSize: configurationStep === 'artworkSize' ? 'open' : 'closed',
    artworkImage: configurationStep === 'artworkImage' ? 'open' : 'closed',
    frameStyle: configurationStep === 'frameStyle' ? 'open' : 'closed',
    passepartout: configurationStep === 'passepartout' ? 'open' : 'closed',
    glass: configurationStep === 'glass' ? 'open' : 'closed',
    spacer: configurationStep === 'spacer' ? 'open' : 'closed',
  }

  return stepStates
}

/**
 * Determines whether frame builder step is enabled.
 */
export const getIsStepEnabled = (
  frameBuilderType: SanityFrameBuilderType,
  frameBuilderConfiguration: FrameBuilderConfigurationWithLimits,
  state: FrameBuilderState,
  step: FrameBuilderStep,
) => {
  const isFloatingFrame = !!state.profile?.floatingFrame

  switch (step) {
    case 'artworkSize': {
      return true
    }

    case 'artworkImage': {
      return frameBuilderType !== 'passepartout'
    }

    case 'frameStyle': {
      return frameBuilderType !== 'passepartout'
    }

    case 'passepartout': {
      return frameBuilderType === 'passepartout' || !isFloatingFrame
    }

    case 'glass': {
      return frameBuilderType !== 'passepartout' && !isFloatingFrame
    }

    case 'spacer': {
      const spacerSizes = frameBuilderConfiguration.spacerSizes.filter(
        (spacerSize) => {
          return (
            !state.profile ||
            spacerSize.thickness === 0 ||
            spacerSize.mouldingIds?.some(
              (mouldingId) => mouldingId === state.profile?._id,
            )
          )
        },
      )
      const hasSpacerOptions = spacerSizes.some(
        (spacerSize) => spacerSize.thickness > 0,
      )

      return (
        frameBuilderType !== 'passepartout' &&
        !isFloatingFrame &&
        hasSpacerOptions
      )
    }
  }
}
